import React, { useState } from 'react';
import { useSendOtpMutation, useVerifyOtpMutation } from '../redux/api/roomBookingApi';
import { useNavigate, useLocation } from 'react-router-dom';
import useCustomModal from '../hooks/useCustomModal';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";

    // OTP Login State
    const [otpPhone, setOtpPhone] = useState('');
    const [otpValue, setOtpValue] = useState('');
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [sendOtp, { isLoading: isSendOtpLoading }] = useSendOtpMutation();
    const [verifyOtp, { isLoading: isVerifyOtpLoading }] = useVerifyOtpMutation();
    const [isTestingOtpEnabled, setIsTestingOtpEnabled] = useState(false);

    // Custom Modal Hook
    const { showModal, CustomModalComponent } = useCustomModal();

    // Side effect to fetch settings
    React.useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/settings`)
            .then(res => res.json())
            .then(data => {
                if (data && data.status === 'success' && data.data) {
                    setIsTestingOtpEnabled(data.data.is_testing_otp_enabled === true);
                }
            })
            .catch(err => console.error("Error fetching settings for login:", err));
    }, []);

    // Auto-fill phone if testing enabled
    React.useEffect(() => {
        if (isTestingOtpEnabled && !otpPhone) {
            setOtpPhone('9845098450');
        }
    }, [isTestingOtpEnabled, otpPhone]);

    const handleSendOtp = async (e) => {
        e.preventDefault();
        try {
            const res = await sendOtp({ phone: otpPhone }).unwrap();
            if (process.env.REACT_APP_ENABLE_SANDBOX_OTP === 'true') {
                showModal(`OTP for accessing your account at Divyakshetra Hariharapura is ${res.otp}`, 'success');
            } else {
                showModal(`OTP Sent to ${otpPhone}`, 'success');
            }
            setIsOtpSent(true);
            
            // If testing enabled, show OTP in alert and auto-fill
            if (isTestingOtpEnabled && res.otp) {
                setOtpValue(res.otp);
            }
        } catch (err) {
            console.error(err);
            showModal(err?.data?.message || 'Failed to send OTP', 'error');
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        try {
            const res = await verifyOtp({ phone: otpPhone, otp: otpValue }).unwrap();
            if (res.token) {
                localStorage.setItem('uid', res.token);
                localStorage.setItem('user', JSON.stringify(res.user));
                showModal('Login Successful!', 'success', () => navigate(from));
            } else {
                showModal("Verification success but no token received: " + res.message, 'warning');
            }
        } catch (err) {
            console.error(err);
            showModal(err?.data?.message || 'Invalid OTP', 'error');
        }
    };

    return (
        <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9f9f9', padding: '50px 0' }}>
            <CustomModalComponent />
            <div style={{ maxWidth: '400px', width: '90%', backgroundColor: '#fff', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                <div style={{ padding: '30px' }}>
                    <form onSubmit={isOtpSent ? handleVerifyOtp : handleSendOtp}>
                        <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#840000' }}>OTP Login</h3>
                        <div className="mb-3">
                            <label className="form-label">Phone Number</label>
                            <input
                                type="text"
                                className="form-control"
                                value={otpPhone}
                                onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, ''))}
                                required
                                maxLength="10"
                                placeholder="Enter your phone number"
                                disabled={isOtpSent}
                            />
                        </div>
                        {isOtpSent && (
                            <div className="mb-3">
                                <label className="form-label">Enter OTP</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={otpValue}
                                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ''))}
                                    required
                                    maxLength="6"
                                    placeholder="Enter the 6-digit OTP"
                                />
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn w-100"
                            disabled={isSendOtpLoading || isVerifyOtpLoading}
                            style={{ backgroundColor: '#ffd54f', color: '#840000', fontWeight: 'bold' }}
                        >
                            {isOtpSent ? (isVerifyOtpLoading ? 'Verifying...' : 'Verify OTP') : (isSendOtpLoading ? 'Sending OTP...' : 'Send OTP')}
                        </button>
                        {isOtpSent && (
                            <div className="text-center mt-3">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setIsOtpSent(false)}
                                    style={{ color: '#840000' }}
                                >
                                    Use a different number
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
